import React, { useState, useEffect, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import useAlertDialog from "../../../hooks/useAlertDialog";
import { AlertType } from "../../../hooks/common/useAttachAlertDialog";
import moment from "moment";
import useLoadedUser from "../../../hooks/auth/useLoadedUser";
import { UserRole } from "../../../../models/User";
import Partner, { nhanTrangThaiPartner } from "../../../../models/Partner";
import useUpdateActivePartnerAdmin from "../hook/UpdateActiveUserAdmin";
import {
	deletePartnerAdmin,
	patchPartnerStationLandingSectionFlags,
	setPartnerChainMode,
	unlockPartnerPayoutLock,
} from "../../../../services/PartnerService";
import useBanPartnerAdmin from "../hook/BanPartnerAdmin";
import DialogPartnerPayoutLock from "./DialogPartnerPayoutLock";
import DialogPartnerPayoutLockAudit from "./DialogPartnerPayoutLockAudit";
import DialogImpersonateStation from "./DialogImpersonateStation";
import { HIEN_CONG_TAC_TAB_TRANG_KHACH, NHAN_LOAI, suyLoaiTaiKhoan, tenChuoi } from "./cheDoGon";
import { getStationArchiveCheck, postStationArchive } from "../../../../services/PartnerService";
import DialogStationStatus from "./DialogStationStatus";
import { useAdminPermissions } from "../../../hooks/useAdminPermissions";

const moneyCellFormatter = new Intl.NumberFormat("en-US", {
	useGrouping: true,
	minimumFractionDigits: 2,
	maximumFractionDigits: 8,
});

function formatMoneyCell(amount: number | null | undefined) {
	const num = typeof amount === "number" && Number.isFinite(amount) ? amount : 0;
	return moneyCellFormatter.format(num);
}

/* ── cell styles ── */
const cell: React.CSSProperties = {
	padding: "8px 6px",
	textAlign: "center",
	borderBottom: "1px solid #e8e8e8",
	borderRight: "1px solid #f5f5f5",
	fontSize: "12.5px",
	color: "#333",
	verticalAlign: "middle",
};

const cellMoney: React.CSSProperties = {
	...cell,
	fontFamily: "'Consolas', 'Courier New', monospace",
	fontWeight: 500,
	textAlign: "right",
	paddingRight: "10px",
};

const textClip: React.CSSProperties = {
	maxWidth: "140px",
	overflow: "hidden",
	textOverflow: "ellipsis",
	whiteSpace: "nowrap",
	display: "inline-block",
};

/* ───────────────────────────────────────────────
   Portal-based Action Menu 
   Renders OUTSIDE the table DOM via React Portal,
   so overflow:auto on the table cannot clip it.
   ─────────────────────────────────────────────── */

interface ActionMenuItem {
	label: string;
	icon: string;
	onClick: () => void;
	color?: string;
	dividerAfter?: boolean;
	/** RBAC permission key required to show this item. Omit = always visible. */
	permission?: string;
	/** Tên nhóm hiện thành tiêu đề nhỏ phía trên item đầu của nhóm (bản gọn 28/08). */
	nhom?: string;
}

/** ~px per menu row (button + padding); keeps scroll height estimate stable without measuring DOM */
const ACTION_MENU_ROW_APPROX_PX = 44;
const ACTION_MENU_VIEW_MARGIN = 12;

function ActionMenu({
	items,
	anchorRef,
	isOpen,
	onClose,
}: {
	items: ActionMenuItem[];
	anchorRef: React.RefObject<HTMLButtonElement>;
	isOpen: boolean;
	onClose: () => void;
}) {
	const menuRef = useRef<HTMLDivElement>(null);
	const itemsRef = useRef(items);
	itemsRef.current = items;
	const [pos, setPos] = useState({ top: 0, left: 0, openUp: false, maxHeight: 360 });

	const placeMenu = useCallback(() => {
		if (!anchorRef.current) return;
		const latest = itemsRef.current;
		const rect = anchorRef.current.getBoundingClientRect();
		const menuWidth = 180;
		const vh = window.innerHeight;
		const estimatedFull =
			latest.length * ACTION_MENU_ROW_APPROX_PX + latest.filter((i) => i.dividerAfter).length * 12 + 20;
		const cap = Math.max(120, vh - ACTION_MENU_VIEW_MARGIN * 2);
		const naturalHeight = Math.min(estimatedFull, cap);

		const spaceBelow = vh - rect.bottom - ACTION_MENU_VIEW_MARGIN;
		const spaceAbove = rect.top - ACTION_MENU_VIEW_MARGIN;

		let openUp = false;
		let maxHeight = naturalHeight;

		if (naturalHeight <= spaceBelow) {
			openUp = false;
			maxHeight = naturalHeight;
		} else if (naturalHeight <= spaceAbove) {
			openUp = true;
			maxHeight = naturalHeight;
		} else {
			openUp = spaceAbove >= spaceBelow;
			maxHeight = Math.max(140, Math.min(naturalHeight, openUp ? spaceAbove : spaceBelow));
		}

		setPos({
			top: openUp ? rect.top : rect.bottom + 4,
			left: Math.min(rect.left, window.innerWidth - menuWidth - ACTION_MENU_VIEW_MARGIN),
			openUp,
			maxHeight,
		});
	}, [anchorRef]);

	// Position + max height when menu opens; refit on resize / viewport changes (small screens)
	useEffect(() => {
		if (!isOpen || !anchorRef.current) return;
		placeMenu();
		const onResizeOrViewport = () => placeMenu();
		let rafId: number | null = null;
		const onAnyScroll = () => {
			if (rafId != null) return;
			rafId = window.requestAnimationFrame(() => {
				rafId = null;
				placeMenu();
			});
		};
		window.addEventListener("resize", onResizeOrViewport);
		window.addEventListener("scroll", onAnyScroll, true);
		window.visualViewport?.addEventListener("resize", onResizeOrViewport);
		window.visualViewport?.addEventListener("scroll", onResizeOrViewport);
		return () => {
			window.removeEventListener("resize", onResizeOrViewport);
			window.removeEventListener("scroll", onAnyScroll, true);
			window.visualViewport?.removeEventListener("resize", onResizeOrViewport);
			window.visualViewport?.removeEventListener("scroll", onResizeOrViewport);
			if (rafId != null) {
				window.cancelAnimationFrame(rafId);
			}
		};
	}, [isOpen, anchorRef, placeMenu, items.length]);

	// Close on outside click
	useEffect(() => {
		if (!isOpen) return;
		const handler = (e: MouseEvent) => {
			const target = e.target as Node;
			if (
				menuRef.current &&
				!menuRef.current.contains(target) &&
				anchorRef.current &&
				!anchorRef.current.contains(target)
			) {
				onClose();
			}
		};
		// Use setTimeout so the opening click doesn't immediately close it
		const timer = setTimeout(() => document.addEventListener("mousedown", handler), 0);
		return () => {
			clearTimeout(timer);
			document.removeEventListener("mousedown", handler);
		};
	}, [isOpen, onClose, anchorRef]);

	if (!isOpen) return null;

	const menu = (
		<div
			ref={menuRef}
			role="menu"
			style={{
				position: "fixed",
				top: pos.openUp ? "auto" : pos.top,
				bottom: pos.openUp ? window.innerHeight - pos.top + 4 : "auto",
				left: pos.left,
				zIndex: 99999,
				minWidth: "170px",
				maxHeight: pos.maxHeight,
				overflowY: "auto",
				overflowX: "hidden",
				WebkitOverflowScrolling: "touch",
				boxSizing: "border-box",
				backgroundColor: "#fff",
				borderRadius: "10px",
				boxShadow: "0 12px 40px rgba(0,0,0,0.18), 0 2px 6px rgba(0,0,0,0.08)",
				border: "1px solid #e2e5ea",
				padding: "4px 0",
				animation: "fadeInMenu 0.15s ease-out",
			}}>
			<style>{`
				@keyframes fadeInMenu {
					from { opacity: 0; transform: translateY(${pos.openUp ? "6px" : "-6px"}); }
					to   { opacity: 1; transform: translateY(0); }
				}
			`}</style>
			{items.map((item, idx) => (
				<React.Fragment key={idx}>
					{item.nhom && item.nhom !== items[idx - 1]?.nhom && (
						<div
							style={{
								padding: "7px 12px 3px",
								fontSize: "10.5px",
								fontWeight: 700,
								letterSpacing: "0.6px",
								textTransform: "uppercase",
								color: "#9aa4b2",
							}}>
							{item.nhom}
						</div>
					)}
					<button
						style={{
							display: "flex",
							alignItems: "center",
							gap: "10px",
							width: "100%",
							padding: "9px 16px",
							border: "none",
							background: "transparent",
							cursor: "pointer",
							fontSize: "13px",
							fontWeight: 500,
							color: item.color || "#374151",
							textAlign: "left",
							borderRadius: "0",
							transition: "background 0.12s",
						}}
						onMouseEnter={(e) => (e.currentTarget.style.background = "#f3f4f6")}
						onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
						onClick={() => {
							item.onClick();
							onClose();
						}}>
						<span style={{ fontSize: "15px", width: "20px", textAlign: "center" }}>{item.icon}</span>
						{item.label}
					</button>
					{item.dividerAfter && <div style={{ borderTop: "1px solid #e5e7eb", margin: "4px 12px" }} />}
				</React.Fragment>
			))}
		</div>
	);

	// Portal renders the menu at document.body level — fully outside the table
	return ReactDOM.createPortal(menu, document.body);
}

/* ───────────────────────────────────────────────
   PartnerTableBody (main row component)
   ─────────────────────────────────────────────── */

/**
 * Trang khách. Đặt thành hằng vì Admin chưa có biến môi trường nào trỏ tới nó —
 * đổi tên miền thì sửa đúng một dòng này.
 */
/** Có phải link http(s) không — chỉ khi đó mới rút thành nút. */
function coLaLink(v?: string | null): boolean {
	return /^https?:\/\//i.test(String(v ?? "").trim());
}

const TRANG_KHACH = "https://whalelo.net";

/** Nút link nhỏ trong ô bảng. Dùng thẻ <a> chứ không phải <button>: bấm thì mở tab mới,
 *  mà chuột phải vẫn "Copy link address" được — đúng nhu cầu gửi link cho khách. */
const nutLink: React.CSSProperties = {
	display: "inline-flex",
	alignItems: "center",
	gap: 3,
	padding: "2px 7px",
	borderRadius: 6,
	fontSize: "11px",
	fontWeight: 600,
	lineHeight: 1.5,
	textDecoration: "none",
	whiteSpace: "nowrap",
};

/** Màu chip trạng thái — một nơi giữ, thêm trạng thái mới chỉ phải thêm một dòng. */
const STATUS_CHIP_STYLE: Record<string, { background: string; color: string; border: string }> = {
	Published: { background: "#e3f2fd", color: "#1565c0", border: "1px solid #90caf9" },
	Actived: { background: "#e8f5e9", color: "#2e7d32", border: "1px solid #a5d6a7" },
	Inactive: { background: "#eceff1", color: "#546e7a", border: "1px solid #b0bec5" },
	Banned: { background: "#ffebee", color: "#b71c1c", border: "1px solid #ef9a9a" },
	Pending: { background: "#ede7f6", color: "#4527a0", border: "1px solid #b39ddb" },
};

export default function PartnerTableBody(props: {
	val: Partner;
	/** Tên chuỗi Admin đặt ở màn Legal entities & chains (17/10/2026). */
	tenChuoiDaDat?: string;
	/** Bên ký của trạm (17/10/2026). */
	benKy?: { legalEntityId: number | null; legalName: string | null; status: string | null };
	/** true = bảng gọn đang dựng thử (28/08). */
	gon?: boolean;
	index: number;
	reload: () => void;
	setIsDialogLoginOpen: (val: boolean) => void;
	setOrderId: (val: string) => void;
	setIsDialogIfOpen: (val: boolean) => void;
	setOrderIdInfo: (val: string) => void;
	setIsDialogImages: (val: boolean) => void;
	setIsDialogReviews: (val: boolean) => void;
	setIsDialogTrustItems: (val: boolean) => void;
	setIsDialogFeatures: (val: boolean) => void;
	setIsDialogWalkingDistance: (val: boolean) => void;
	setIsDialogFinancialOpen: (val: boolean) => void;
	setFinancialPartner: (val: Partner | null) => void;
	onOpenBan: (id: string) => void;
	selected?: boolean;
	onToggleSelect?: (id: string, checked: boolean) => void;
}) {
	const gon = props.gon === true;
	const { user } = useLoadedUser();
	const { hasPermission } = useAdminPermissions();
	const { openAlertDialog } = useAlertDialog();
	const { ActiveUserAd } = useUpdateActivePartnerAdmin({ reload: props.reload });
	const { unbanPartnerAd } = useBanPartnerAdmin({ reload: props.reload });
	const [menuOpen, setMenuOpen] = useState(false);
	const btnRef = useRef<HTMLButtonElement>(null);
	const [payoutLockDialogOpen, setPayoutLockDialogOpen] = useState(false);
	const [payoutAuditDialogOpen, setPayoutAuditDialogOpen] = useState(false);
	const [impersonateDialogOpen, setImpersonateDialogOpen] = useState(false);
	// MỘT CỬA đổi trạng thái — thay 3 mục rời Activate/Ban/Unban.
	const [statusDialogOpen, setStatusDialogOpen] = useState(false);

	const p = props.val;

	// Nhãn trạng thái cho mục menu. Phải khai báo TRƯỚC khi dựng menu, vì menu đọc nó.
	//
	// Quân chốt 25/08: viết TIẾNG ANH và trùng khít giá trị lưu trong DB — "Actived"
	// chứ không phải "Active", vì cột `lifecycleStatus` lưu `actived`. Sai ngữ pháp
	// một chút, đổi lại người soi dữ liệu không phải dịch ngược nhãn ↔ giá trị.
	// Đọc THẲNG `status` server trả về. Từ 25/08 server gọi `getLifecycleStatus` nên
	// `status` đã đủ 5 giá trị, kể cả `banned` — không phải tự ghép từ `isBanned`/`active`
	// nữa. Bớt một nơi giữ luật.
	// Nhánh dự phòng KHÔNG được đoán bừa. Trước đây rơi vào đây là ghi "Inactive",
	// nên 5 hồ sơ `pending` bị hiện thành "Inactive" — nhân viên tưởng station đã
	// đóng nên không ai đi duyệt (đo prod 28/08/2026). Trạng thái lạ thì hiện
	// THẲNG giá trị thật để người soi biết ngay là có giá trị chưa được đặt nhãn.
	const statusLabel = nhanTrangThaiPartner(p);
	const currency = p.destinationCurrencyCode || p.destination?.currencyCode || "";

	const closeMenu = useCallback(() => setMenuOpen(false), []);

	const handleUpdateStatusStaff = (id: string, status: boolean) => {
		openAlertDialog?.(
			AlertType.Confirm,
			"You definitely want to change the state of the facility?",
			() => {},
			() => ActiveUserAd(id || "0", status),
			undefined,
			{ declineText: "Cancel", acceptText: "Confirm" }
		);
	};

	const handleUnbanPartner = () => {
		openAlertDialog?.(
			AlertType.Confirm,
			"Remove ban for this station? Station login and APIs will work again.",
			() => {},
			() => void unbanPartnerAd(p.id || "0"),
			undefined,
			{ declineText: "Cancel", acceptText: "Unban" }
		);
	};

	/**
	 * Admin đặt tài khoản là CHUỖI / ĐIỂM LẺ (Quân chốt 08/10/2026).
	 * BẬT: dựng Store nếu chưa có rồi ghi cờ ⇒ chủ thấy tab Locations + quyền công nợ chuỗi.
	 * TẮT: chỉ ghi cờ, không xoá Store, không gỡ điểm ⇒ bật lại là nguyên như cũ.
	 */
	const datCheDoChuoi = (id: string, bat: boolean) => {
		const ten = p.name || `#${id}`;
		openAlertDialog?.(
			AlertType.Confirm,
			bat
				? `Set "${ten}" as a CHAIN OWNER? The account gets the Stations tab and chain payout settings. ` +
				  "Stations already linked to this account's chain become stations of the chain. Nothing is deleted."
				: `Set "${ten}" as a SINGLE STATION? Chain mode is turned off: no Stations tab, linked stations manage their own payouts again. ` +
				  "The chain record and links are kept, so you can turn chain mode back on later.",
			() => {},
			async () => {
				try {
					const kq = await setPartnerChainMode(id, bat);
					openAlertDialog?.(
						AlertType.Success,
						kq.chainModeEnabled ? "Chain mode enabled." : "Chain mode disabled — account is now a single station.",
						() => props.reload()
					);
				} catch (err: unknown) {
					const msg =
						err && typeof err === "object" && "response" in err
							? (err as { response?: { data?: { message?: string } } }).response?.data?.message
							: undefined;
					openAlertDialog?.(AlertType.Fail, msg || "Could not change chain mode.");
				}
			},
			undefined,
			{ declineText: "Cancel", acceptText: bat ? "Set as chain owner" : "Set as single station" }
		);
	};

	const handleUnlockPayout = (id: string) => {
		openAlertDialog?.(
			AlertType.Confirm,
			"Unlock manual payout requests for this station? The station will be able to request payout again from the portal.",
			() => {},
			async () => {
				try {
					await unlockPartnerPayoutLock(id);
					openAlertDialog?.(AlertType.Success, "Payout unlocked.", () => props.reload());
				} catch (err: unknown) {
					const msg =
						err && typeof err === "object" && "response" in err
							? (err as { response?: { data?: { message?: string } } }).response?.data?.message
							: undefined;
					openAlertDialog?.(AlertType.Fail, msg || "Unlock failed.");
				}
			},
			undefined,
			{ declineText: "Cancel", acceptText: "Unlock" }
		);
	};

	/**
	 * ── VÒNG 3 (CGO ↔ COO) ────────────────────────────────────────────────────
	 * COO: `Delete station` là nút nguy hiểm nhất mà lại đứng sẵn trong menu — một
	 * cú trượt tay là mất bản ghi mà đơn cũ còn trỏ tới, hỏng sổ sách.
	 * CGO: nhưng vẫn phải dọn được station rác.
	 * ⇒ Archive thay cho Delete ở việc thường ngày. Archive KHÔNG xoá gì, đảo ngược
	 *   được; chỉ sau 90 ngày + sạch cả ba (0 đơn · 0 công nợ · 0 chữ ký) mới thành
	 *   ứng viên xoá, và bước xoá vẫn do người bấm.
	 *
	 * Hỏi máy TRƯỚC khi hiện hộp xác nhận: nêu thẳng lý do không cho, thay vì làm
	 * mờ nút rồi để nhân viên tự đoán.
	 */
	const luuTruStation = async (id: string) => {
		try {
			const kiem = await getStationArchiveCheck(id);
			if (!kiem.canArchive) {
				// Nêu THẲNG lý do. Làm mờ nút rồi để nhân viên tự đoán là cách nhanh
				// nhất để họ đi hỏi vòng quanh.
				openAlertDialog?.(AlertType.Fail, (kiem.reasons || []).join(" ") || "Not eligible to archive.");
				return;
			}
			openAlertDialog?.(
				AlertType.Confirm,
				`Archive "${p.name || `#${id}`}"? It is set to Closed and hidden from operating lists. ` +
					"Nothing is deleted — the station can still sign in, see history and withdraw the remaining balance. " +
					"It becomes a deletion candidate only after 90 days, and only with no bookings, no balance and no signature.",
				() => {},
				async () => {
					const kq = await postStationArchive(id);
					if (!kq.ok) {
						openAlertDialog?.(AlertType.Fail, (kq.reasons || []).join(" ") || "Not eligible to archive.");
						return;
					}
					props.reload();
				},
				undefined,
				{ declineText: "Cancel", acceptText: "Archive" }
			);
		} catch {
			openAlertDialog?.(AlertType.Fail, "Could not reach the archive service.");
		}
	};

	const handleDeletePartner = (id: string) => {
		openAlertDialog?.(
			AlertType.Confirm,
			"Delete this station permanently? All linked orders, settlements, payment requests, and logs for this station will be removed. This cannot be undone.",
			() => {},
			async () => {
				try {
					await deletePartnerAdmin(id);
					openAlertDialog?.(AlertType.Success, "Station deleted.", () => {
						props.reload();
					});
				} catch (err: unknown) {
					const msg =
						err && typeof err === "object" && "response" in err
							? (err as { response?: { data?: { message?: string } } }).response?.data?.message
							: undefined;
					openAlertDialog?.(AlertType.Fail, msg || "Delete failed.");
				}
			},
			undefined,
			{ declineText: "Cancel", acceptText: "Delete" }
		);
	};

	// Build menu items
	const menuItemsRaw: ActionMenuItem[] = [
		// ── Nhóm CHỈ XEM ──────────────────────────────────────────────────────────
		// Đặt trên cùng và kẻ tách khỏi phần dưới: mọi mục còn lại đều THAY ĐỔI dữ liệu
		// (Deactivate / Ban / Lock payout / Delete). Để một nút "xem" nằm lẫn giữa chúng
		// là mời gọi bấm nhầm.
		{
			icon: "👁️",
			// Tài khoản chủ chuỗi mở ra MÀN KHÁC HẲN (danh sách điểm + phân quyền), nên
			// gọi nó là "station" thì nhân viên tưởng bấm nhầm sang một điểm lẻ.
			label: suyLoaiTaiKhoan(p) === "chuChuoi" ? "View as chain owner" : "View as station",
			nhom: gon ? "Xem" : undefined,
			color: "#0b5f9e",
			onClick: () => setImpersonateDialogOpen(true),
			permission: "station.impersonate.read",
		},
		{
			icon: "📋",
			// "Info" quá mơ hồ — đây là màn XEM TOÀN BỘ hồ sơ station, đúng thứ Quân
			// cần khi mở một dòng ra kiểm. Đặt ngay dưới "View as", cùng nhóm Xem.
			label: "Station detail",
			nhom: gon ? "Xem" : undefined,
			color: "#0b5f9e",
			onClick: () => {
				props.setIsDialogIfOpen(true);
				props.setOrderIdInfo(p.id || "0");
			},
		},
		{
			icon: "📄",
			label: gon ? "Portal logs" : "View station logs",
			nhom: gon ? "Xem" : undefined,
			color: "#0b5f9e",
			onClick: () => {
				// Màn hình log tự đọc ?partnerId= từ URL và lọc sẵn — xem StationPortalLogs.tsx
				window.open(
					`/station-portal-logs?partnerId=${encodeURIComponent(p.id || "")}`,
					"_blank",
					"noopener,noreferrer"
				);
			},
			dividerAfter: true,
			permission: "analytics.station-portal-logs.view",
		},
		// ── Nhóm THAY ĐỔI ─────────────────────────────────────────────────────────
		// ── MỘT CỬA đổi trạng thái ──────────────────────────────────────────────
		// Thay cho 3 mục rời trước đây: Activate/Deactivate · Ban station · Unban station.
		// Quân chốt 24/08: gom về một nút, sổ xuống 5 trạng thái, mỗi cái kèm điều kiện.
		// Nhãn hiện luôn trạng thái đang có ⇒ biết mà không cần mở hộp thoại.
		{
			icon: "🔄",
			label: `Change Status · ${statusLabel}`,
			nhom: gon ? "Sửa" : undefined,
			color: "#16467E",
			onClick: () => setStatusDialogOpen(true),
			permission: "partners.manage",
		},
		{
			icon: "✏️",
			label: "Edit",
			nhom: gon ? "Sửa" : undefined,
			onClick: () => {
				props.setIsDialogLoginOpen(true);
				props.setOrderId(p.id || "0");
			},
			permission: "partners.manage",
		},
		// ── Chuỗi / Điểm lẻ (Quân chốt 08/10) ──────────────────────────────────
		// Một mục, nhãn đảo theo trạng thái: đang là chủ chuỗi (bật) ⇒ cho về điểm lẻ;
		// còn lại (điểm lẻ, chủ chuỗi đang TẮT) ⇒ cho bật chuỗi. Điểm THUỘC chuỗi không
		// có mục này: muốn tách điểm thì sửa ở "Email Partner" trong Edit như trước.
		...(suyLoaiTaiKhoan(p) === "trongChuoi" || suyLoaiTaiKhoan(p) === "trongChuoiTat"
			? []
			: ([
					{
						icon: "🏢",
						label: suyLoaiTaiKhoan(p) === "chuChuoi" ? "Set as single station" : "Set as chain owner",
						nhom: gon ? "Sửa" : undefined,
						color: "#7b1fa2",
						onClick: () => datCheDoChuoi(p.id || "0", suyLoaiTaiKhoan(p) !== "chuChuoi"),
						permission: "partners.manage",
					},
			  ] as ActionMenuItem[])),
		{
			icon: "💰",
			label: "Financial",
			nhom: gon ? "Tiền" : undefined,
			onClick: () => {
				props.setFinancialPartner(p);
				props.setIsDialogFinancialOpen(true);
			},
			dividerAfter: true,
			permission: "partners.manage",
		},
		// Ban / Unban nay nằm trong hộp thoại "Đổi trạng thái" — không còn mục rời.
		// `Lock payout` CỐ Ý giữ nguyên: nó dùng quyền settlements.manage, không phải
		// trạng thái vòng đời mà là biện pháp tài chính.
		...(p.payoutLocked
			? ([
					{
						icon: "✏️",
						label: "Update payout lock reason",
						nhom: gon ? "Tiền" : undefined,
						color: "#c62828",
						onClick: () => setPayoutLockDialogOpen(true),
						permission: "settlements.manage",
					},
					{
						icon: "📜",
						label: "View payout lock audit",
						nhom: gon ? "Tiền" : undefined,
						onClick: () => setPayoutAuditDialogOpen(true),
						permission: "settlements.view",
					},
					{
						icon: "🔓",
						label: "Unlock payout",
						nhom: gon ? "Tiền" : undefined,
						color: "#2e7d32",
						onClick: () => handleUnlockPayout(p.id || "0"),
						permission: "settlements.manage",
					},
			  ] as ActionMenuItem[])
			: ([
					{
						icon: "🔒",
						label: "Lock payout (contract violation)",
						nhom: gon ? "Tiền" : undefined,
						color: "#c62828",
						onClick: () => setPayoutLockDialogOpen(true),
						permission: "settlements.manage",
					},
			  ] as ActionMenuItem[])),

		{
			icon: "📸",
			label: "Images",
			nhom: gon ? "__BO__" : undefined,
			onClick: () => {
				props.setIsDialogImages(true);
				props.setOrderId(p.id || "0");
			},
			permission: "partners.manage",
		},
		{
			icon: "⭐",
			label: "Reviews",
			nhom: gon ? "__BO__" : undefined,
			onClick: () => {
				props.setIsDialogReviews(true);
				props.setOrderId(p.id || "0");
			},
			permission: "partners.manage",
		},
		{
			icon: "📝",
			// Nhãn "Steps" không nói lên nó sửa cái gì; hộp bên trong ghi "Manage Process
			// Steps" và ghi vào `features` — 255/302 station đang dùng, KHÔNG bỏ được.
			label: "Process steps",
			nhom: gon ? "__BO__" : undefined,
			onClick: () => {
				props.setIsDialogFeatures(true);
				props.setOrderId(p.id || "0");
			},
			permission: "partners.manage",
		},
		{
			icon: "🚶",
			label: "Walking distance",
			nhom: gon ? "__BO__" : undefined,
			onClick: () => {
				props.setIsDialogWalkingDistance(true);
				props.setOrderId(p.id || "0");
			},
			permission: "partners.manage",
		},
		{
			icon: "📦",
			label: "Archive station",
			nhom: gon ? "Kết thúc" : undefined,
			color: "#5d4037",
			onClick: () => void luuTruStation(p.id || "0"),
			permission: "partners.manage",
		},
		{
			icon: "🗑️",
			// Bản gọn KHÔNG hiện Delete: việc thường ngày là Archive (đảo ngược được).
			// Xoá hẳn chỉ làm ở màn "ứng viên xoá" sau 90 ngày, nơi máy đã kiểm sạch
			// cả ba điều kiện — chứ không phải một dòng nằm sẵn trong menu mỗi hàng.
			label: "Delete station",
			nhom: gon ? "__BO__" : undefined,
			color: "#b71c1c",
			onClick: () => handleDeletePartner(p.id || "0"),
			permission: "partners.manage",
		},
	];

	// Filter by RBAC permission — items without permission always show (read-only Info)
	const menuItemsLoc = menuItemsRaw
		.filter((item) => !item.permission || hasPermission(item.permission))
		// "__BO__" = mục đã chuyển đi chỗ khác ở bản gọn (nội dung trang khách nay
		// nằm trong Station detail; Delete nằm ở màn ứng viên xoá).
		.filter((item) => !(gon && item.nhom === "__BO__"));

	// Bản gọn: xếp lại theo nhóm để tiêu đề nhóm hiện đúng MỘT lần mỗi nhóm.
	// Thứ tự cố ý: xem trước (không đổi gì) → sửa → nội dung → tiền → nguy hiểm cuối cùng.
	const THU_TU_NHOM = ["Xem", "Sửa", "Tiền", "Kết thúc"];
	const menuItems = gon
		? menuItemsLoc.map((it, i) => ({ it, i }))
				.sort((a, b) => {
					const va = THU_TU_NHOM.indexOf(a.it.nhom || "");
					const vb = THU_TU_NHOM.indexOf(b.it.nhom || "");
					return va === vb ? a.i - b.i : (va < 0 ? 99 : va) - (vb < 0 ? 99 : vb);
				})
				.map(({ it }, idx, arr) => ({
					...it,
					// Kẻ vạch ở ranh giới nhóm; bỏ vạch cũ để không bị hai vạch dính nhau.
					dividerAfter: idx < arr.length - 1 && arr[idx + 1].it.nhom !== it.nhom,
				}))
		: menuItemsLoc;

	const rowBg = props.selected ? "#eaeff5" : props.index % 2 === 0 ? "#ffffff" : "#f8f9fb";

	return (
		<>
			<DialogPartnerPayoutLock
				openDialog={payoutLockDialogOpen}
				onClose={() => setPayoutLockDialogOpen(false)}
				partner={p}
				onSuccess={() => props.reload()}
			/>
			<DialogPartnerPayoutLockAudit
				openDialog={payoutAuditDialogOpen}
				onClose={() => setPayoutAuditDialogOpen(false)}
				partnerId={p.id || null}
				partnerName={p.name || p.displayName || `#${p.id}`}
			/>
			<DialogStationStatus
				openDialog={statusDialogOpen}
				setOpenDialog={setStatusDialogOpen}
				partnerId={p.id || null}
				reload={props.reload}
			/>
			<DialogImpersonateStation
				openDialog={impersonateDialogOpen}
				setOpenDialog={setImpersonateDialogOpen}
				partnerId={p.id || null}
				stationName={p.name || p.displayName || `#${p.id}`}
			/>
			<tr
				style={{ background: rowBg, transition: "background 0.15s" }}
				onMouseEnter={(e) => (e.currentTarget.style.background = props.selected ? "#eaeff5" : "#e3f2fd")}
				onMouseLeave={(e) => (e.currentTarget.style.background = rowBg)}>
				{/* ── Station group ── */}
				<td style={{ ...cell, width: "40px" }}>
					<input
						type="checkbox"
						style={{ cursor: "pointer", width: "16px", height: "16px", accentColor: "#1a237e" }}
						checked={props.selected}
						onChange={(e) => {
							props.onToggleSelect?.(p.id || "", e.target.checked);
						}}
					/>
				</td>
				<td style={{ ...cell, fontWeight: 700, color: "#1a237e", fontSize: "12px" }}>{p.id}</td>
				<td style={cell}>
					{/* MỘT ô nói trọn trạng thái. Trước 25/08 thông tin bị xé làm hai cột: ô này
					    ghi "Inactive" cho station bị cấm (SAI — lệnh ban ghi active=false nên nó rơi
					    nhầm vào nhánh cuối), còn cột "Ban" bên cạnh mới ghi BANNED. Quân chốt 25/08
					    phương án A đầy đủ: gộp một ô, lý do + ngày cấm đưa vào tooltip. */}
					<span
						title={
							p.isBanned
								? `${p.banReason || "Banned"}${
										p.bannedAt ? `\n${moment(p.bannedAt).format("YYYY-MM-DD HH:mm")}` : ""
								  }`
								: undefined
						}
						style={{
							display: "inline-block",
							padding: "2px 10px",
							borderRadius: "12px",
							fontSize: "11px",
							fontWeight: p.isBanned ? 700 : 600,
							...(STATUS_CHIP_STYLE[statusLabel] || STATUS_CHIP_STYLE.Inactive),
						}}>
						{statusLabel}
					</span>
					{gon && Number(p.contractViolationCount) > 0 ? (
						<span
							title="Vi phạm hợp đồng (admin)"
							style={{
								display: "inline-block",
								marginLeft: 5,
								padding: "2px 7px",
								borderRadius: "10px",
								fontSize: "10.5px",
								fontWeight: 700,
								background: Number(p.contractViolationCount) >= 3 ? "#ffebee" : "#fff3e0",
								color: Number(p.contractViolationCount) >= 3 ? "#b71c1c" : "#e65100",
								border: "1px solid rgba(0,0,0,0.06)",
							}}>
							Vi phạm {Math.min(3, Math.trunc(Number(p.contractViolationCount)))}
						</span>
					) : null}
				</td>
				<td style={cell}>
					{p.payoutLocked ? (
						<span
							title="Manual payout requests disabled"
							style={{
								display: "inline-block",
								marginRight: 6,
								padding: "1px 6px",
								borderRadius: 6,
								fontSize: "9px",
								fontWeight: 700,
								background: "#ffebee",
								color: "#b71c1c",
								border: "1px solid #ef9a9a",
								verticalAlign: "middle",
							}}>
							PAYOUT LOCKED
						</span>
					) : null}
					<span
						style={textClip}
						title={p.name || ""}>
						{p.name || "—"}
					</span>
					{gon && p.displayName && p.displayName !== p.name ? (
						<div
							style={{ ...textClip, display: "block", fontSize: "11px", color: "#78909c", marginTop: "1px" }}
							title={p.displayName || ""}>
							{p.displayName}
						</div>
					) : null}
				</td>
				{gon ? (
					<td style={{ ...cell, whiteSpace: "nowrap" }}>
						{(() => {
							const loai = suyLoaiTaiKhoan(p);
							const n = NHAN_LOAI[loai];
							// 17/10/2026: tên chuỗi Admin đặt đứng trước email chủ chuỗi.
							const chuoi = props.tenChuoiDaDat || tenChuoi(p);
							const bk = props.benKy;
							return (
								<>
									<span
										style={{
											display: "inline-block",
											padding: "2px 9px",
											borderRadius: "10px",
											fontSize: "11px",
											fontWeight: 600,
											background: n.nen,
											color: n.mau,
											border: `1px solid ${n.vien}`,
										}}>
										{n.chu}
									</span>
									{loai !== "single" && chuoi ? (
										<div
											style={{ ...textClip, display: "block", fontSize: "11px", color: "#78909c", marginTop: "1px", maxWidth: "150px" }}
											title={chuoi}>
											{chuoi}
										</div>
									) : null}
									{bk && bk.legalEntityId ? (
										<a
											href={`/legal-entities`}
											style={{ ...textClip, display: "block", fontSize: "11px", color: "#475467", marginTop: "1px", maxWidth: "150px", textDecoration: "none" }}
											title={`Legal entity #${bk.legalEntityId}: ${bk.legalName || ""}${bk.status === "verified" ? " (verified)" : ""}`}>
											{bk.status === "verified" ? "✓ " : ""}
											{bk.legalName || `Legal entity #${bk.legalEntityId}`}
										</a>
									) : null}
								</>
							);
						})()}
					</td>
				) : (
				<td style={cell}>
					<span
						style={textClip}
						title={p.displayName || ""}>
						{p.displayName || "—"}
					</span>
				</td>
				)}
				{!gon && (
				<td style={{ ...cell, textAlign: "center", whiteSpace: "nowrap" }}>
					{(() => {
						const v = Math.min(3, Math.max(0, Math.trunc(Number(p.contractViolationCount) || 0)));
						if (v === 0) {
							return <span style={{ color: "#9e9e9e", fontSize: "11px" }}>—</span>;
						}
						return (
							<span
								title="Vi phạm hợp đồng (admin)"
								style={{
									display: "inline-block",
									padding: "2px 8px",
									borderRadius: "10px",
									fontSize: "11px",
									fontWeight: 700,
									background: v >= 3 ? "#ffebee" : "#fff3e0",
									color: v >= 3 ? "#b71c1c" : "#e65100",
									border: "1px solid rgba(0,0,0,0.06)",
								}}>
								Lần {v}
							</span>
						);
					})()}
				</td>
				)}
				{HIEN_CONG_TAC_TAB_TRANG_KHACH && (
				<td style={{ ...cell, padding: "4px" }}>
					<input
						type="checkbox"
						title="Show Meet your Station on website"
						checked={p.stationShowMeetTab !== false}
						onChange={async (e) => {
							try {
								await patchPartnerStationLandingSectionFlags(p.id || "", {
									stationShowMeetTab: e.target.checked,
								});
								props.reload();
							} catch {
								alert("Could not update Meet tab flag");
							}
						}}
					/>
				</td>
				)}
				{HIEN_CONG_TAC_TAB_TRANG_KHACH && (
				<td style={{ ...cell, padding: "4px" }}>
					<input
						type="checkbox"
						title="Show Nearby Attractions on website"
						checked={p.stationShowNearbyTab !== false}
						onChange={async (e) => {
							try {
								await patchPartnerStationLandingSectionFlags(p.id || "", {
									stationShowNearbyTab: e.target.checked,
								});
								props.reload();
							} catch {
								alert("Could not update Nearby tab flag");
							}
						}}
					/>
				</td>
				)}

				{/* ── Account group ── */}
				<td style={{ ...cell, fontSize: "11.5px" }}>
					<span
						style={{ ...textClip, maxWidth: "160px" }}
						title={p.user?.email || ""}>
						{p.user?.email || "—"}
					</span>
					{gon && p.store?.storeOwnerUser?.email ? (
						<div
							style={{ ...textClip, display: "block", maxWidth: "160px", fontSize: "10.5px", color: "#78909c", marginTop: "1px" }}
							title={`Owner: ${p.store.storeOwnerUser.email}`}>
							owner · {p.store.storeOwnerUser.email}
						</div>
					) : null}
				</td>
				{!gon && (
				<td style={{ ...cell, fontSize: "11.5px" }}>
					<span
						style={{ ...textClip, maxWidth: "160px" }}
						title={p.store?.storeOwnerUser?.email || ""}>
						{p.store?.storeOwnerUser?.email || "—"}
					</span>
				</td>
				)}

				{/* ── Address group ── */}
				<td style={cell}>
					{/* Quân chốt 26/08: rút link dài thành nút nhỏ, và thêm nút mở thẳng trang
					    đặt dịch vụ — bấm phát là sang, tiện gửi link cho khách.

					    ⚠️ 39/299 station có `address` là ĐỊA CHỈ CHỮ chứ không phải link, và vài
					    cái trong đó `displayAddress` đang rỗng. Biến tất cả thành nút là mất
					    thông tin của họ — nên chỉ đổi khi thật sự là link. */}
					<div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
						{coLaLink(p.address) ? (
							<a
								href={p.address || "#"}
								target="_blank"
								rel="noopener noreferrer"
								title={p.address || ""}
								style={{
									...nutLink,
									background: "#e8f5e9",
									color: "#2e7d32",
									border: "1px solid #a5d6a7",
								}}>
								📍 Map
							</a>
						) : (
							<span
								style={{ ...textClip, maxWidth: "130px" }}
								title={p.address || ""}>
								{p.address || "—"}
							</span>
						)}
						<a
							href={`${TRANG_KHACH}/order?locationId=${encodeURIComponent(p.id || "")}`}
							target="_blank"
							rel="noopener noreferrer"
							title="Mở trang đặt dịch vụ của station này — chuột phải để copy link gửi khách"
							style={{
								...nutLink,
								background: "#e3f2fd",
								color: "#1565c0",
								border: "1px solid #90caf9",
							}}>
							🔗 Book
						</a>
					</div>
					{gon && p.displayAddress ? (
						<div
							style={{ ...textClip, display: "block", maxWidth: "190px", fontSize: "11px", color: "#78909c", marginTop: "2px" }}
							title={p.displayAddress || ""}>
							{p.displayAddress}
						</div>
					) : null}
				</td>
				{!gon && (
				<td style={cell}>
					<span
						style={{ ...textClip, maxWidth: "150px" }}
						title={p.displayAddress || ""}>
						{p.displayAddress || "—"}
					</span>
				</td>
				)}

				{/* ── Financial group — bản gọn dời hết vào Actions → Financial ── */}
				{!gon && (
				<>
				<td style={cellMoney}>
					{p.stationCom != null && p.stationCom > 0 ? (
						<span style={{ color: "#e65100" }}>{formatMoneyCell(p.stationCom)}</span>
					) : (
						<span style={{ color: "#9e9e9e", fontStyle: "italic", fontFamily: "inherit" }}>
							Country Rate
						</span>
					)}
				</td>
				<td style={cellMoney}>
					{p.minimumPayout != null && p.minimumPayout > 0 ? (
						<span style={{ color: "#6a1b9a" }}>{formatMoneyCell(p.minimumPayout)}</span>
					) : (
						<span style={{ color: "#9e9e9e", fontStyle: "italic", fontFamily: "inherit" }}>
							Country Rate
						</span>
					)}
				</td>
				<td style={cellMoney}>
					{p.pubRate != null && p.pubRate > 0 ? (
						<span style={{ color: "#00695c" }}>{formatMoneyCell(p.pubRate)}</span>
					) : (
						<span style={{ color: "#9e9e9e", fontStyle: "italic", fontFamily: "inherit" }}>
							Country Rate
						</span>
					)}
				</td>
				<td style={cellMoney}>
					<span style={{ color: "#1565c0" }}>{formatMoneyCell(p.paidAmount)}</span>
				</td>
				<td style={cellMoney}>
					<span>{formatMoneyCell(p.paidAmountLocal)}</span>
				</td>

				{/* ── Rate Limits group ── */}
				<td style={cellMoney}>{`${formatMoneyCell(p.rateLimitMin)} ${currency}`.trim()}</td>
				<td style={cellMoney}>{`${formatMoneyCell(p.rateLimitMax)} ${currency}`.trim()}</td>
				</>
				)}

				{/* ── Meta group ── */}
				<td style={{ ...cell, fontSize: "11.5px", whiteSpace: "nowrap", color: "#616161" }}>
					{p.createdAt ? moment(p.createdAt).format("HH:mm DD/MM/YY") : "—"}
				</td>

				{/* ── Actions ── */}
				<td style={{ ...cell, borderRight: "none", padding: "4px 6px" }}>
					{user?.role !== UserRole.Partner && (
						<>
							<button
								ref={btnRef}
								onClick={() => setMenuOpen((o) => !o)}
								style={{
									display: "inline-flex",
									alignItems: "center",
									gap: "6px",
									padding: "6px 14px",
									borderRadius: "6px",
									fontSize: "12px",
									fontWeight: 600,
									cursor: "pointer",
									border: "1px solid #d1d5db",
									background: menuOpen ? "#f0f0f0" : "#fff",
									color: "#374151",
									transition: "all 0.15s",
									boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
								}}>
								⚙️ Actions {menuOpen ? "▲" : "▼"}
							</button>
							<ActionMenu
								items={menuItems}
								anchorRef={btnRef}
								isOpen={menuOpen}
								onClose={closeMenu}
							/>
						</>
					)}
				</td>
			</tr>
		</>
	);
}
